// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import snakeCase from 'lodash/snakeCase';
import { useTranslation } from 'react-i18next';
import { DashboardsPoolResources } from 'util/Types';
import { mergeClasses, normalizeNumber } from 'util/commons';
import styles from './PoolDashboardResourceTable.module.scss';
import CustomPagination from 'components/atoms/CustomPagination';
import { getFormatDateWithCurrentTimezone } from 'ts/formatDate';
import { DATE_FORMAT } from 'util/ConstantValues';

type PoolDashboardResourceTableProps = {
  dashboardPoolResources: DashboardsPoolResources[];
  total: number;
  page: number;
  onPageChange: (page: number) => void;
};

const PoolDashboardResourceTable: React.FC<PoolDashboardResourceTableProps> = ({
  dashboardPoolResources,
  total,
  page,
  onPageChange,
}) => {
  const { t, i18n } = useTranslation();
  const pageCount = Math.ceil(total / 50);

  return (
    <div>
      <table className={styles.PoolDashboardResourceTable}>
        <thead>
          <tr>
            <th>{t('pool_dashboard.resources_table.header.emission_id')}</th>
            <th className={styles.SmallCol}>
              {t('pool_dashboard.resources_table.header.date')}
            </th>
            <th>{t('pool_dashboard.resources_table.header.item')}</th>
            <th>{t('pool_dashboard.resources_table.header.material')}</th>
            <th>{t('pool_dashboard.resources_table.header.waste')}</th>
            <th className={styles.StationCol}>
              {t('pool_dashboard.resources_table.header.stations')}
            </th>
            <th>{t('pool_dashboard.resources_table.header.site_name')}</th>
            <th className={styles.SmallCol}>
              {t('pool_dashboard.resources_table.header.site_type')}
            </th>
            <th>{t('pool_dashboard.resources_table.header.building_type')}</th>
            <th>
              {t('pool_dashboard.resources_table.header.collect_company')}
            </th>
            <th>{t('pool_dashboard.resources_table.header.collected_date')}</th>
            <th>{t('pool_dashboard.resources_table.header.compression')}</th>
            <th
              className={styles.SmallCol}
              style={{
                textAlign: 'right',
              }}
            >
              {t('pool_dashboard.resources_table.header.weight')}
            </th>
          </tr>
        </thead>

        <tbody>
          {dashboardPoolResources.length > 0 ? (
            dashboardPoolResources.map((item, index) => (
              <tr key={index}>
                <td className={styles.SmallCol}>
                  <p>{item.id}</p>
                </td>
                <td className={styles.SmallCol}>
                  <p>
                    {getFormatDateWithCurrentTimezone(
                      item.emissionDatetime,
                      DATE_FORMAT.slaYMDHms
                    )}
                  </p>
                </td>
                <td className={styles.SmallCol}>
                  <p>{item.wasteUnit?.name}</p>
                </td>
                <td>
                  <p>
                    {i18n.language === 'ja'
                      ? item.materialName
                      : item.englishMaterialName || item.materialName}
                  </p>
                </td>
                <td>
                  <p>
                    {t(
                      `pool_dashboard.filter.${snakeCase(item.wasteTypeMaster)}`
                    )}
                  </p>
                </td>
                <td className={styles.StationCol}>
                  <p>{item.siteStations.map(({ name }) => name).join(', ')}</p>
                </td>
                <td>
                  <p>{item.siteName}</p>
                </td>
                <td className={styles.SmallCol}>
                  <p>{item.siteType}</p>
                </td>
                <td className={styles.SmallCol}>
                  <p>{item.siteBuildingType}</p>
                </td>
                <td>
                  <p>
                    {item.collectionSite?.length
                      ? item.collectionSite.map(({ name }) => name).join(', ')
                      : t('pool_dashboard.filter.no_collection_linkage_option')}
                  </p>
                </td>
                <td className={styles.SmallCol}>
                  <p>
                    {item.receivedDatetime &&
                      getFormatDateWithCurrentTimezone(
                        item.receivedDatetime,
                        DATE_FORMAT.slaYMDHms
                      )}
                  </p>
                </td>
                <td className={styles.SmallCol}>
                  <p>{item.compressionSite?.name}</p>
                </td>
                <td className={mergeClasses(styles.SmallCol, styles.Weight)}>
                  <p>{normalizeNumber({ value: item.weight })}</p>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={13} className={styles.noData}>
                {t('messages.M_008')}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {pageCount > 1 && (
        <CustomPagination
          className={styles.Pagination}
          forcePage={Math.max(page - 1, 0)}
          pageCount={pageCount}
          onPageChange={(selectedItem) => {
            onPageChange(selectedItem.selected + 1);
          }}
        />
      )}
    </div>
  );
};

export default PoolDashboardResourceTable;
